<script setup lang="ts">
/**
 * THE kit's action menu behind a button — the „more actions“ (⋯) menu of a
 * page or workspace header, a row's overflow menu. The trigger is an icon
 * button (`icon` slot, `MoreHorizontal` by default) named by `label`; the menu
 * is the kit menu panel (top layer, flips and shifts inside the viewport).
 *
 * Put `NxContextMenuItem`s into the default slot — icon (`#icon`), label,
 * `danger`, `disabled`, `shortcut`, `checked` — with `NxContextMenuSeparator`
 * and `NxContextMenuLabel` between groups. Selecting an item closes the menu.
 *
 * Keyboard: Enter/Space/ArrowDown on the trigger open the menu with focus on
 * the first item; ArrowUp/ArrowDown/Home/End walk the items; Escape closes and
 * returns focus to the trigger; Tab closes. A pointer down outside closes too.
 * `v-model:open` is optional. A fully custom trigger goes into `#trigger`
 * (slot props: `open`, `toggle`, `attrs` — bind `attrs` onto the button).
 */
import MoreHorizontal from 'lucide-vue-next/dist/esm/icons/ellipsis.js';
import type { Placement } from '@floating-ui/vue';
import { computed, ref, useId, watch } from 'vue';
import type { NxButtonSize, NxButtonVariant } from '../Button';
import NxMenuPanel from '../ContextMenu/NxMenuPanel.vue';
import NxIconButton from '../IconButton/NxIconButton.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    /** Accessible name and tooltip of the trigger; also names the menu. */
    label: string;
    /** Trigger height. Defaults to `s` (header density). */
    height?: NxButtonSize;
    /** Trigger variant. Defaults to `ghost`. */
    variant?: NxButtonVariant;
    disabled?: boolean;
    /** Where the menu opens. Defaults to `bottom-end` (right-aligned under the trigger). */
    placement?: Placement;
    /** Menu width. Defaults to `auto` (content-sized). */
    menuWidth?: 'sm' | 'md' | 'lg' | 'auto';
  }>(),
  {
    height: 's',
    variant: 'ghost',
    disabled: false,
    placement: 'bottom-end',
    menuWidth: 'auto',
  }
);

const open = defineModel<boolean>('open', { default: false });

const menuId = `nx-menu-button-${useId()}`;
const anchor = ref<HTMLElement | null>(null);
/** The focusable trigger inside the anchor (the icon button, or the custom trigger). */
const triggerElement = computed<HTMLElement | null>(
  () => anchor.value?.querySelector<HTMLElement>('button, [tabindex]') ?? anchor.value
);

function toggle() {
  if (props.disabled) return;
  open.value = !open.value;
}

function onTriggerKeydown(event: KeyboardEvent) {
  if (event.key !== 'ArrowDown' || open.value || props.disabled) return;
  event.preventDefault();
  open.value = true;
}

// A trigger that becomes unavailable while its menu is open closes it.
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) open.value = false;
  }
);

const triggerAttrs = computed(() => ({
  'aria-haspopup': 'menu' as const,
  'aria-expanded': open.value,
  'aria-controls': open.value ? menuId : undefined,
  onClick: toggle,
  onKeydown: onTriggerKeydown,
}));
</script>

<template>
  <span ref="anchor" class="nx-menu-button" data-testid="nx-menu-button" v-bind="$attrs">
    <slot name="trigger" :open="open" :toggle="toggle" :attrs="triggerAttrs">
      <NxIconButton
        v-bind="triggerAttrs"
        :height="props.height"
        :variant="props.variant"
        :disabled="props.disabled"
        :title="props.label"
        :aria-label="props.label"
        :class="{ 'is-open': open }"
      >
        <slot name="icon">
          <MoreHorizontal class="nxt-icon-md" aria-hidden="true" />
        </slot>
      </NxIconButton>
    </slot>
  </span>
  <NxMenuPanel
    :id="menuId"
    :open="open"
    :reference="triggerElement"
    :placement="props.placement"
    :offset="4"
    :width="props.menuWidth"
    :aria-label="props.label"
    :ignore="[anchor]"
    :return-focus-to="triggerElement"
    data-testid="nx-menu-button-menu"
    @close="open = false"
  >
    <slot />
  </NxMenuPanel>
</template>

<style scoped>
.nx-menu-button {
  display: inline-flex;
  flex: 0 0 auto;
}
</style>
